import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { selectAllEvents, selectEventsTotal } from '@app/main/main-screen/store/data/events/events.selectors';
import { LdTabs } from '@app/main/leak-detection/enums/ld-tabs';
import { ICorrelation } from '@app/api/interfaces/correlation.interface';
import { IEvent } from '@app/api/interfaces/event.interface';
import {
  LdActionPlace,
  LdActionType,
} from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.actions';
import { LdCoordinatorStore } from '@app/main/leak-detection/services/leak-detection-coordinator/leak-detection-coordinator.store';
import { DoToolbarService } from '@app/main/main-content/display-options/services/do-toolbar/do-toolbar.service';
import { DoFiltersService } from '@app/main/main-content/display-options/services/do-filters/do-filters.service';
import { LdLoggerFiltersService } from '@app/main/leak-detection/services/ld-logger-filters/ld-logger-filters.service';
import {
  isCorrelationVisible,
  isEventVisible,
  isLoggerVisible,
} from '@app/main/leak-detection/utils/ld-filter-visibility';
import { DoFilterKey } from '@app/main/main-content/display-options/enums/do-filter-key.enum';
import { combineLatest, map } from 'rxjs';
import { LdDataTimedService } from '@app/main/leak-detection/services/leak-detection-data-timed/leak-detection-data-timed.service';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { IMainTableCellContext } from '@app/main/main-content/main-table/interfaces/main-table-cell-context.interface';

@Injectable({ providedIn: 'root' })
export class LeakDetectionTabsPanelService {
  private store = inject(Store);
  private ldCoordinatorStore = inject(LdCoordinatorStore);
  private doToolbarService = inject(DoToolbarService);
  private doFiltersService = inject(DoFiltersService);
  private ldLoggerFiltersService = inject(LdLoggerFiltersService);
  private ldDataTimedService = inject(LdDataTimedService);

  public readonly activeTab$ = this.ldCoordinatorStore.activeTab$;
  public readonly scrollToId$ = this.ldCoordinatorStore.scrollToId$;

  public totalCorrelationsCount$ = this.ldDataTimedService.correlations$.pipe(map(correlations => correlations.length));

  public totalLoggersCount$ = this.ldDataTimedService.ldLoggers$.pipe(
    map(loggerDeployments => loggerDeployments.length)
  );

  public totalEventsCount$ = this.store.select(selectEventsTotal);

  public readonly correlations$ = combineLatest([
    this.ldDataTimedService.correlations$,
    this.doToolbarService.syncTableWithMapFilter,
    this.doFiltersService.getFilter(DoFilterKey.Correlation),
  ]).pipe(
    map(([correlations, syncTableWithMapFilter, correlationFilter]) => {
      return syncTableWithMapFilter
        ? correlations.filter(correlation => isCorrelationVisible(correlationFilter, correlation))
        : correlations;
    })
  );

  public readonly loggers$ = combineLatest([
    this.ldDataTimedService.ldLoggers$,
    this.doToolbarService.syncTableWithMapFilter,
    this.ldLoggerFiltersService.getLoggerFilters(),
  ]).pipe(
    map(([ldLoggers, syncTableWithMapFilter, loggerFilters]) => {
      return syncTableWithMapFilter
        ? ldLoggers.filter(ldLogger => isLoggerVisible(loggerFilters, ldLogger))
        : ldLoggers;
    })
  );
  public readonly events$ = combineLatest([
    this.store.select(selectAllEvents),
    this.doToolbarService.syncTableWithMapFilter,
    this.doFiltersService.getFilter(DoFilterKey.Events),
  ]).pipe(
    map(([events, syncTableWithMapFilter, eventFilter]) => {
      return syncTableWithMapFilter ? events.filter(event => isEventVisible(eventFilter, event)) : events;
    })
  );

  public readonly selectedLoggersMap = this.ldCoordinatorStore.selectedLoggersMapS;
  public readonly selectedCorrelationsMap = this.ldCoordinatorStore.selectedCorrelationsMapS;
  public readonly selectedEventsMap = this.ldCoordinatorStore.selectedEventsMapS;

  public onTabChange(tab: LdTabs): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.TabChange,
      place: LdActionPlace.TabsPanel,
      tab,
    });
  }

  public onLoggerClick(logger: ILdLogger): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.LoggerClick,
      place: LdActionPlace.TabsPanel,
      item: logger,
    });
  }

  public onLoggerCtrlClick(logger: ILdLogger): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.LoggerCtrlClick,
      place: LdActionPlace.TabsPanel,
      item: logger,
    });
  }

  public onLoggerDbClick(item: ILdLogger): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.LoggerDbClick,
      place: LdActionPlace.TabsPanel,
      item,
    });
  }

  public onCorrelationClick(correlation: ICorrelation): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.CorrelationClick,
      place: LdActionPlace.TabsPanel,
      item: correlation,
    });
  }

  public onCorrelationCtrlClick(correlation: ICorrelation): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.CorrelationCtrlClick,
      place: LdActionPlace.TabsPanel,
      item: correlation,
    });
  }

  public onCorrelationDbClick(item: ICorrelation): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.CorrelationDbClick,
      place: LdActionPlace.TabsPanel,
      item,
    });
  }

  public onEventClick(event: IEvent): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.EventClick,
      place: LdActionPlace.TabsPanel,
      item: event,
    });
  }

  public onEventDbClick(item: IEvent): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.EventDbClick,
      place: LdActionPlace.TabsPanel,
      item,
    });
  }

  public onLoggerContextMenu(item: ILdLogger, event?: MouseEvent, cell?: IMainTableCellContext): void {
    if (!event) return;

    this.ldCoordinatorStore.dispatch({
      type: LdActionType.LoggerContextMenu,
      place: LdActionPlace.TabsPanel,
      item,
      event,
      cell,
    });
  }

  public onCorrelationContextMenu(item: ICorrelation, event?: MouseEvent, cell?: IMainTableCellContext): void {
    if (!event) return;

    this.ldCoordinatorStore.dispatch({
      type: LdActionType.CorrelationContextMenu,
      place: LdActionPlace.TabsPanel,
      item,
      event,
      cell,
    });
  }

  public onEventContextMenu(item: IEvent, event?: MouseEvent, cell?: IMainTableCellContext): void {
    if (!event) return;

    this.ldCoordinatorStore.dispatch({
      type: LdActionType.EventContextMenu,
      place: LdActionPlace.TabsPanel,
      item,
      event,
      cell,
    });
  }

  public onZoomToFitItem(tab: LdTabs, item: ICorrelation | ILdLogger | IEvent): void {
    this.ldCoordinatorStore.dispatch({
      type: LdActionType.ZoomToFitItem,
      tab,
      item,
    });
  }
}
